import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLang } from "../context/LangContext";
import Layout from "../components/Layout";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { TrendingUp, MessageSquare, Bot, CheckCircle, Clock, Users, Globe, Wifi, WifiOff, Monitor, Smartphone, ExternalLink, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#1c91ef", "#e11d48", "#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6"];


function LiveVisitors({ siteId }) {
  const [data, setData] = useState({ online: 0, visitors: [] });
  const [loading, setLoading] = useState(true);
  const timerRef = useRef();

  const fetch = () => {
    const url = siteId ? `${API}/analytics/online?site_id=${siteId}` : `${API}/analytics/online`;
    axios.get(url)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetch();
    timerRef.current = setInterval(fetch, 30000);
    return () => clearInterval(timerRef.current);
  }, [siteId]);

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${data.online > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
            <h2 className="font-semibold text-slate-900">Canlı Ziyaretçiler</h2>
          </div>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${data.online > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {data.online} çevrimiçi
          </span>
        </div>
        <button onClick={fetch} className="text-xs text-slate-400 hover:text-blue-600 transition-colors flex items-center gap-1">
          <Wifi size={12} /> Yenile
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400 text-sm animate-pulse">Yükleniyor...</div>
      ) : data.visitors.length === 0 ? (
        <div className="text-center py-10">
          <WifiOff size={32} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Şu an aktif ziyaretçi yok</p>
          <p className="text-slate-300 text-xs mt-1">Son 5 dakika içinde siteye gelen ziyaretçiler burada görünür</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="pb-3 pl-2 font-medium">IP Adresi</th>
                <th className="pb-3 font-medium">Konum</th>
                <th className="pb-3 font-medium">Cihaz / Tarayıcı</th>
                <th className="pb-3 font-medium">Bulunduğu Sayfa</th>
                <th className="pb-3 font-medium">Referrer</th>
                <th className="pb-3 font-medium text-right pr-2">Süre</th>
              </tr>
            </thead>
            <tbody>
              {data.visitors.map((v) => (
                <tr key={v.session_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors group">
                  <td className="py-3 pl-2">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                      <span className="font-mono text-xs text-slate-700">{v.ip}</span>
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-1.5">
                      {v.flag && <span className="text-base leading-none">{v.flag}</span>}
                      <div>
                        <div className="text-slate-700 text-xs font-medium">{v.country}</div>
                        {v.city !== "—" && <div className="text-slate-400 text-xs">{v.city}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-1.5">
                      {v.device === "Mobile" ? (
                        <Smartphone size={12} className="text-slate-400 flex-shrink-0" />
                      ) : (
                        <Monitor size={12} className="text-slate-400 flex-shrink-0" />
                      )}
                      <div>
                        <div className="text-slate-700 text-xs font-medium">{v.device || "Desktop"}</div>
                        <div className="text-slate-400 text-xs">{v.browser || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 max-w-[200px]">
                    {v.current_page && v.current_page !== "—" ? (
                      <a href={v.current_page} target="_blank" rel="noreferrer"
                        title={v.current_page}
                        className="flex items-center gap-1 text-blue-600 hover:underline text-xs group/ap">
                        <Globe size={11} className="flex-shrink-0 text-slate-400" />
                        <span className="truncate max-w-[160px]">
                          {v.current_title ||
                            (v.current_page.replace(/^https?:\/\/[^/]+/, "") || "/")}
                        </span>
                        <ExternalLink size={10} className="flex-shrink-0 opacity-0 group-hover/ap:opacity-100 transition-opacity" />
                      </a>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="py-3 max-w-[140px]">
                    {v.referrer !== "—" ? (
                      <span className="text-xs text-slate-500 truncate block" title={v.referrer}>
                        {v.referrer.replace(/^https?:\/\//, '').split('/')[0]}
                      </span>
                    ) : <span className="text-slate-300 text-xs">Direkt</span>}
                  </td>
                  <td className="py-3 text-right pr-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v.minutes_on_site > 5 ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                      {v.minutes_on_site < 1 ? '<1 dk' : `${v.minutes_on_site} dk`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-300 mt-3 text-right">Her 30 saniyede otomatik yenilenir</p>
    </div>
  );
}

export default function Analytics() {
  const { t } = useLang();
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitorLoading, setVisitorLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null); // null = tüm siteler

  useEffect(() => {
    axios.get(`${API}/sites`).then(({ data }) => setSites(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setVisitorLoading(true);
    const siteParam = selectedSiteId ? `&site_id=${selectedSiteId}` : "";
    axios.get(`${API}/analytics?period=${period}${siteParam}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
    axios.get(`${API}/analytics/visitors?period=${period}${siteParam}`)
      .then(({ data }) => setVisitorData(data))
      .finally(() => setVisitorLoading(false));
  }, [period, selectedSiteId]);

  const PERIODS = [
    { key: "today", label: "Bugün" },
    { key: "yesterday", label: "Dün" },
    { key: "7d", label: t("analytics.period_7") },
    { key: "30d", label: t("analytics.period_30") },
    { key: "90d", label: t("analytics.period_90") },
  ];

  const pieData = data ? [
    { name: t("analytics.ai_rate"), value: data.ai_handled || 0 },
    { name: "Human", value: (data.total_conversations || 0) - (data.ai_handled || 0) },
  ] : [];

  const statCards = data ? [
    {
      icon: MessageSquare,
      label: t("analytics.total"),
      value: data.total_conversations,
      color: "text-blue-600 bg-blue-50",
    },
    {
      icon: Clock,
      label: t("analytics.open"),
      value: data.open_conversations,
      color: "text-amber-600 bg-amber-50",
    },
    {
      icon: CheckCircle,
      label: t("analytics.resolved"),
      value: data.resolved_conversations,
      color: "text-emerald-600 bg-emerald-50",
    },
    {
      icon: Bot,
      label: t("analytics.ai_rate"),
      value: `${data.ai_resolution_rate}%`,
      color: "text-blue-700 bg-purple-50",
    },
  ] : [];

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 animate-fade-in-up flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">{t("analytics.title")}</h1>
            <p className="text-slate-500 mt-1">Detaylı istatistikler ve raporlar</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Site filtresi */}
            {sites.length > 1 && (
              <select
                value={selectedSiteId || ""}
                onChange={e => setSelectedSiteId(e.target.value ? parseInt(e.target.value) : null)}
                className="h-9 rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:outline-none focus:border-blue-400 bg-white"
                data-testid="analytics-site-filter"
              >
                <option value="">Tüm Siteler</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2 bg-white rounded-xl border border-slate-200 p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    period === p.key ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Live Visitors */}
        <LiveVisitors siteId={selectedSiteId} />

        {loading ? (
          <div className="text-center py-16 text-slate-400 animate-pulse">{t("common.loading")}</div>
        ) : !data ? (
          <div className="text-center py-16 text-slate-400">{t("analytics.no_data")}</div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 stagger-children">
              {statCards.map((card, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-6 card-hover animate-fade-in-up" data-testid={`analytics-stat-${i}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${card.color}`}>
                    <card.icon size={20} />
                  </div>
                  <div className="text-2xl font-heading font-bold text-slate-900 mb-1">{card.value}</div>
                  <div className="text-sm text-slate-500">{card.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Daily Volume Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                <h2 className="font-semibold text-slate-900 mb-5">{t("analytics.daily_volume")}</h2>
                {data.daily_chart?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.daily_chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => d.slice(5)}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                        labelFormatter={(l) => `Tarih: ${l}`}
                      />
                      <Bar dataKey="count" fill="#4f46e5" radius={[6, 6, 0, 0]} name="Konuşmalar" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-48 text-slate-400 text-sm">{t("analytics.no_data")}</div>
                )}
              </div>

              {/* AI vs Human Pie */}
              <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                <h2 className="font-semibold text-slate-900 mb-5">AI vs İnsan</h2>
                {data.total_conversations > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                          {pieData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex gap-4 justify-center mt-2">
                      {pieData.map((item, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i] }} />
                          {item.name}: {item.value}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-48 text-slate-400 text-sm">{t("analytics.no_data")}</div>
                )}
              </div>
            </div>

            {/* Agent Performance */}
            {data.agent_performance?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                <h2 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
                  <Users size={18} className="text-blue-500" /> {t("analytics.agent_perf")}
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                        <th className="pb-3 font-medium">{t("analytics.agent")}</th>
                        <th className="pb-3 font-medium text-right">{t("analytics.assigned")}</th>
                        <th className="pb-3 font-medium text-right">{t("analytics.resolved_count")}</th>
                        <th className="pb-3 font-medium text-right">{t("analytics.rate")}</th>
                        <th className="pb-3 font-medium text-right">Değerlendirme</th>
                        <th className="pb-3 font-medium text-right">Ort. Puan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.agent_performance.map((agent) => (
                        <tr key={agent.agent_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`agent-row-${agent.agent_id}`}>
                          <td className="py-3 font-medium text-slate-900 flex items-center gap-2">
                            {agent.agent_id === 0 && <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-semibold">AI</span>}
                            {agent.name}
                          </td>
                          <td className="py-3 text-right text-slate-700">{agent.assigned}</td>
                          <td className="py-3 text-right text-slate-700">{agent.resolved ?? "—"}</td>
                          <td className="py-3 text-right">
                            {agent.resolution_rate != null ? (
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-600 rounded-full" style={{ width: `${agent.resolution_rate}%` }} />
                                </div>
                                <span className="font-semibold text-blue-600 w-10 text-right">{agent.resolution_rate}%</span>
                              </div>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-3 text-right text-slate-600">
                            {agent.rating_count > 0 ? (
                              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-100">{agent.rating_count} değerlendirme</span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="py-3 text-right">
                            {agent.avg_rating != null ? (
                              <div className="flex items-center justify-end gap-1">
                                {[1,2,3,4,5].map((s) => (
                                  <span key={s} className={`text-sm ${s <= Math.round(agent.avg_rating) ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                                ))}
                                <span className="ml-1 text-xs font-semibold text-slate-700">{agent.avg_rating}</span>
                              </div>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Ziyaretçi İstatistikleri ── */}
        <div className="mt-8">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
            <Users size={20} className="text-blue-500" /> Ziyaretçi İstatistikleri
            {visitorData && (
              <span className="ml-2 text-sm font-normal text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                {visitorData.total_visitors} ziyaretçi
              </span>
            )}
          </h2>

          {visitorLoading ? (
            <div className="text-center py-12 text-slate-400 animate-pulse">Yükleniyor...</div>
          ) : !visitorData || visitorData.total_visitors === 0 ? (
            <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
              <Globe size={32} className="text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Henüz ziyaretçi verisi yok</p>
              <p className="text-slate-300 text-xs mt-1">Widget'ınıza ziyaretçi geldikçe istatistikler burada görünür</p>
            </div>
          ) : (
            <>
              {/* Günlük Ziyaretçi Grafiği */}
              <div className="bg-white rounded-xl border border-slate-100 p-6 mb-6 animate-fade-in-up">
                <h3 className="font-semibold text-slate-900 mb-5 text-sm">Günlük Benzersiz Ziyaretçi</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={visitorData.daily_chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }} labelFormatter={(l) => `Tarih: ${l}`} />
                    <Bar dataKey="count" fill="#1c91ef" radius={[6, 6, 0, 0]} name="Ziyaretçi" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Top Referrers */}
                <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                  <h3 className="font-semibold text-slate-900 mb-4 text-sm flex items-center gap-2">
                    <ArrowUpRight size={15} className="text-blue-500" /> Trafik Kaynakları
                  </h3>
                  <div className="space-y-2.5">
                    {visitorData.top_referrers.slice(0, 8).map((r, i) => {
                      const maxCount = visitorData.top_referrers[0]?.count || 1;
                      const pct = Math.round((r.count / maxCount) * 100);
                      return (
                        <div key={i} className="flex items-center gap-2 group">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-xs text-slate-700 font-medium truncate max-w-[130px]" title={r.source}>{r.source}</span>
                              <span className="text-xs text-slate-400 ml-1 flex-shrink-0">{r.count}</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {visitorData.top_referrers.length === 0 && (
                      <p className="text-xs text-slate-400 py-4 text-center">Veri yok</p>
                    )}
                  </div>
                </div>

                {/* Ülkeler */}
                <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                  <h3 className="font-semibold text-slate-900 mb-4 text-sm flex items-center gap-2">
                    <Globe size={15} className="text-emerald-500" /> Ülkeler
                  </h3>
                  <div className="space-y-2.5">
                    {visitorData.top_countries.slice(0, 8).map((c, i) => {
                      const maxCount = visitorData.top_countries[0]?.count || 1;
                      const pct = Math.round((c.count / maxCount) * 100);
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-base w-5 flex-shrink-0">{c.flag || "🌍"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-xs text-slate-700 font-medium truncate max-w-[100px]">{c.country}</span>
                              <span className="text-xs text-slate-400 flex-shrink-0">{c.count}</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#10b981" }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {visitorData.top_countries.length === 0 && (
                      <p className="text-xs text-slate-400 py-4 text-center">Veri yok</p>
                    )}
                  </div>
                </div>

                {/* Cihaz & Tarayıcı */}
                <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                  <h3 className="font-semibold text-slate-900 mb-4 text-sm flex items-center gap-2">
                    <Monitor size={15} className="text-purple-500" /> Cihaz & Tarayıcı
                  </h3>
                  <div className="mb-4">
                    <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Cihaz</p>
                    {visitorData.device_stats.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 mb-1.5">
                        {d.name === "Mobile" ? <Smartphone size={12} className="text-slate-400 flex-shrink-0" /> : <Monitor size={12} className="text-slate-400 flex-shrink-0" />}
                        <span className="text-xs text-slate-700 flex-1">{d.name}</span>
                        <span className="text-xs font-semibold text-slate-600">{d.value}</span>
                        <span className="text-xs text-slate-400 w-8 text-right">{Math.round(d.value / visitorData.total_visitors * 100)}%</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Tarayıcı</p>
                    {visitorData.browser_stats.slice(0, 5).map((b, i) => (
                      <div key={i} className="flex items-center gap-2 mb-1.5">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-xs text-slate-700 flex-1">{b.name}</span>
                        <span className="text-xs font-semibold text-slate-600">{b.value}</span>
                        <span className="text-xs text-slate-400 w-8 text-right">{Math.round(b.value / visitorData.total_visitors * 100)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Şehirler */}
              {visitorData.top_cities?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-100 p-6 animate-fade-in-up">
                  <h3 className="font-semibold text-slate-900 mb-4 text-sm">Şehirler</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {visitorData.top_cities.map((c, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                        <span className="text-sm text-slate-700 font-medium">{c.city}</span>
                        <span className="text-sm font-bold text-blue-600">{c.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
